import {BaseColumnWrap} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import {
  amenitiesSelector,
  checkInTimeFromMetaInfo,
  checkOutTimeFromMetaInfo,
} from '@stencil/hotels-cg-reducer/src/details/selectors';

// HOOKS
import {useSelector, useDispatch} from 'react-redux';
import AmenitiesIconSelector from '../../../../components/AmenitiesIconSelector';
import * as S from './styles/UGCChipsCard.styles';
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';

type UGCPersuasionHeadingType = {children: string};
const UGCPersuasionHeading: React.FC<UGCPersuasionHeadingType> = ({children}) => (
  <S.UGCPersuasionHeadingContainer>
    <S.Text_Heading_UGCPersuasion>{children}</S.Text_Heading_UGCPersuasion>
  </S.UGCPersuasionHeadingContainer>
);

type ChipDataType = {name: string; id: string};
type ChipsType = {chips: Array<ChipDataType>; onChipClick: Function; id: string};
const Chips: React.FC<ChipsType> = ({chips, onChipClick, id}) => {
  const dispatch = useDispatch();

  return (
    <S.ChipsContainer>
      <S.ChipsList>
        {chips.map((chipData, i) => {
          return (
            <>
              {chipData.name ? (
                <li key={`${chipData}-${i}`} title={chipData.name}>
                  <S.ChipText
                    onClick={() => {
                      if (id === 'guests-like') {
                        dispatch(
                          omnitureThunk(
                            omnitureConfig.pages.DetailsUnif,
                            omnitureConfig.events.ctaClick,
                            {
                              cta: {
                                name: `${DetailsPageComponents.UGCChipsCard}_${detailsClick.ugcChipsCard.whatGuestsLikeClick}`,
                                type: omnitureConfig.ctaType.Button,
                                componentName: DetailsPageComponents.UGCChipsCard,
                                destination: '',
                              },
                            },
                          ),
                        );
                      }

                      if (id === 'you-should-know') {
                        dispatch(
                          omnitureThunk(
                            omnitureConfig.pages.DetailsUnif,
                            omnitureConfig.events.ctaClick,
                            {
                              cta: {
                                name: `${DetailsPageComponents.UGCChipsCard}_${detailsClick.ugcChipsCard.whatYouShouldKnowClick}`,
                                type: omnitureConfig.ctaType.Button,
                                componentName: DetailsPageComponents.UGCChipsCard,
                                destination: '',
                              },
                            },
                          ),
                        );
                      }

                      if (chipData.name.includes('more')) {
                        onChipClick();
                      } else {
                        onChipClick(chipData.id);
                      }
                    }}
                  >
                    {chipData.name}
                  </S.ChipText>
                </li>
              ) : (
                ''
              )}
            </>
          );
        })}
      </S.ChipsList>
    </S.ChipsContainer>
  );
};

const AmenityChips = ({chips, onChipClick, checkInTime, checkoutTime, isEntire = false}) => {
  let amenitiesChipsToDisplay = chips.slice(0, isEntire ? 3 : 4);
  const categorizedAmenities = useSelector(amenitiesSelector);

  const dispatch = useDispatch();

  let totalLength = 0;
  for (let index in categorizedAmenities) {
    totalLength =
      totalLength +
      (categorizedAmenities[index] && categorizedAmenities[index].data
        ? categorizedAmenities[index].data.length
        : 0);
  }

  return (
    <BaseColumnWrap>
      <S.AmenitiesList>
        {amenitiesChipsToDisplay.map((item, index) => {
          return (
            <li key={`${item}-${index}`}>
              <AmenitiesIconSelector name={item} />
              <S.AmenitiesText>{item}</S.AmenitiesText>
            </li>
          );
        })}
      </S.AmenitiesList>
      {/* {chips.length > amenitiesChipsToDisplay.length && ( */}
      {amenitiesChipsToDisplay.length < totalLength && (
        <S.ViewAllAmenities
          onClick={() => {
            dispatch(
              omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
                cta: {
                  name: `${DetailsPageComponents.UGCChipsCard}_${detailsClick.ugcChipsCard.TopViewAllAmenitiesClicked}`,
                  type: omnitureConfig.ctaType.Button,
                  componentName: DetailsPageComponents.UGCChipsCard,
                  destination: '',
                },
              }),
            );
            onChipClick();
          }}
        >
          View all amenities
        </S.ViewAllAmenities>
      )}
      {!isEntire && (
        <S.CheckInCheckOutContainer>
          <S.CheckInCheckOutInner>
            <S.CheckInCheckOutLabel>Check-in</S.CheckInCheckOutLabel>
            <S.CheckInCheckOutTime>{checkInTime}</S.CheckInCheckOutTime>
          </S.CheckInCheckOutInner>
          <S.CheckInCheckOutInner>
            <S.CheckInCheckOutLabel>Check-out</S.CheckInCheckOutLabel>
            <S.CheckInCheckOutTime>{checkoutTime}</S.CheckInCheckOutTime>
          </S.CheckInCheckOutInner>
        </S.CheckInCheckOutContainer>
      )}
    </BaseColumnWrap>
  );
};

export type UGCCardProps = {
  heading: string;
  chips: Array<ChipDataType | string>;
  onChipClick: Function;
  type: string;
  id?: string;
  isEntire?: boolean;
};

//Type-guard for chip data type
function isChipDataType(arg: ChipDataType | string): arg is ChipDataType {
  return (
    typeof (arg as ChipDataType).name === 'string' && typeof (arg as ChipDataType).id === 'string'
  );
}

//Type-guard for chip data array
function isChipObjArray(arg: Array<ChipDataType | string>): arg is Array<ChipDataType> {
  return (arg as Array<ChipDataType>).every(item => isChipDataType(item));
}

const UGCCard: React.FC<UGCCardProps> = ({heading, chips, onChipClick, type, id, isEntire}) => {
  const checkInTime = useSelector(checkInTimeFromMetaInfo) || '';
  const checkoutTime = useSelector(checkOutTimeFromMetaInfo) || '';

  return (
    <S.UGCCardContainer>
      <UGCPersuasionHeading>{heading}</UGCPersuasionHeading>
      {type === 'amenity' && (
        <AmenityChips
          chips={chips}
          onChipClick={onChipClick}
          checkInTime={checkInTime}
          checkoutTime={checkoutTime}
          isEntire={isEntire}
        />
      )}
      {chips && isChipObjArray(chips) && type === 'review' && (
        <Chips chips={chips} onChipClick={onChipClick} id={id} />
      )}
    </S.UGCCardContainer>
  );
};

export default UGCCard;
